import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./header.css";
import { Link, useLocation } from "react-router-dom";
import useAuth from "../../api/useAuth";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearState } from "../../redux/slices/dashboard";
import "aos/dist/aos.css";
import AOS from "aos";
import "../../assets/css/wait.scss";

function Header() {
  const [secondaryMenu, setSecondaryMenu] = useState(getSecondaryMenu([]));
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY < lastScrollY || currentScrollY < 50);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  // Update menu when tokenExtract or loginType changes
  useEffect(() => {
    setSecondaryMenu(
      getSecondaryMenu(JSON.parse(localStorage.getItem("tokenExtract")))
    );
  }, [localStorage.getItem("tokenExtract"), localStorage.getItem("loginType")]);

  const setHoverState = () => setIsHovered(true);
  const setHoverLeaveState = () => setIsHovered(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const handleLinkClick = (e) => {
    if (e.target.classList.contains("dropdown-toggle")) {
      e.preventDefault();
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  // Handle pleaseWait class clicks - show spinner like legacy
  // This matches legacy jQuery behavior where clicking .pleaseWait shows spinner
  useEffect(() => {
    const handlePleaseWait = (e) => {
      const target = e.target.closest(".pleaseWait");
      if (target) {
        // Don't show spinner on report pages, elite page, or nmvtis pages
        const isReportPage = window.location.pathname.includes('/report/') || 
                            window.location.pathname.includes('/report');
        const isElitePage = window.location.pathname.includes('/eliteReports') ||
                           window.location.pathname.includes('/elite-reports') ||
                           window.location.pathname === '/eliteReports' ||
                           window.location.pathname.startsWith('/eliteReports');
        const isNmvtisReportPage = window.location.pathname.includes('/nmvtisreport') ||
                                    window.location.pathname.includes('/nmvtis-report');
        const isNmvtisSearchPage = window.location.pathname.includes('/nmvtisReports') ||
                                    window.location.pathname.includes('/nmvtis-reports');
        // Don't show spinner on report pages - let components handle their own spinners
        if (isReportPage || isElitePage || isNmvtisReportPage || isNmvtisSearchPage) {
          return; // Don't show spinner on these pages
        }
        
        // Show spinner immediately for any .pleaseWait element
        const spinner = document.getElementById("spinner");
        const body = document.body;
        const navbar = document.getElementById("main_navbar");
        if (spinner && body) {
          // Disable navbar transition to prevent shaking
          if (navbar) {
            navbar.style.transition = "none";
          }
          body.style.opacity = "0.8";
          body.style.transition = "opacity 0s";
          spinner.style.setProperty("visibility", "visible", "important");
          spinner.style.setProperty("display", "block", "important");
          spinner.className = "spinner";
          
          // Ensure text spans are visible with important flags
          const spans = spinner.querySelectorAll("span");
          spans.forEach((span, index) => {
            span.style.setProperty("visibility", "visible", "important");
            span.style.setProperty("opacity", "1", "important");
            span.style.setProperty("color", "black", "important");
            span.style.setProperty("display", "inline-block", "important");
            span.style.setProperty("font-size", "12px", "important");
            span.style.setProperty("font-weight", "bold", "important");
            // Ensure text content exists
            if (index === 0 && (!span.textContent || span.textContent.trim() === "")) {
              span.textContent = "Your request is being processed.";
            }
            if (index === 1 && (!span.textContent || span.textContent.trim() === "")) {
              span.textContent = "Please be patient.";
            }
          });
          
          // If no spans, ensure HTML structure is correct
          if (spans.length === 0 && spinner.innerHTML.trim() === "") {
            const waitGifPath = require("../../assets/img/wait.gif");
            spinner.innerHTML = `
              <br />
              <span>Your request is being processed.</span>
              <br />
              <span>Please be patient.</span>
              <br />
              <img class="img-spinner" src="${waitGifPath}" alt="Please wait..Loading" />
            `;
          }
        }
      }
    };

    // Use capture phase to ensure this runs before React's onClick handlers
    document.addEventListener("click", handlePleaseWait, true);
    return () => {
      document.removeEventListener("click", handlePleaseWait, true);
    };
  }, []);

  return (
    <>
      <div
        className="nav-mega-overlay"
        style={{ visibility: isHovered ? "visible" : "hidden" }}
      ></div>
      <Navbar
        isVisible={isVisible}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        handleLinkClick={handleLinkClick}
        secondaryMenu={secondaryMenu}
        setHoverState={setHoverState}
        setHoverLeaveState={setHoverLeaveState}
        isHovered={isHovered}
      />
    </>
  );
}

Navbar.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  isMobileMenuOpen: PropTypes.bool.isRequired,
  toggleMobileMenu: PropTypes.func.isRequired,
  handleLinkClick: PropTypes.func.isRequired,
  secondaryMenu: PropTypes.array.isRequired,
  setHoverState: PropTypes.func.isRequired,
  setHoverLeaveState: PropTypes.func.isRequired,
  isHovered: PropTypes.bool.isRequired,
};

function Navbar({
  isVisible,
  isMobileMenuOpen,
  toggleMobileMenu,
  handleLinkClick,
  secondaryMenu,
  setHoverState,
  setHoverLeaveState,
  isHovered,
}) {
  const isAuthenticated = useAuth();
  const [hideNavbar, setHideNavbar] = useState(false);

  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > lastScrollY && currentScrollY > 75) {
      setHideNavbar(true); // Scrolling down
    } else {
      setHideNavbar(false); // Scrolling up
    }

    setLastScrollY(currentScrollY);
  };

  const throttle = (func, delay) => {
    let lastCall = 0;
    return (...args) => {
      const now = new Date().getTime();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  };

  useEffect(() => {
    const throttledScroll = throttle(handleScroll, 200);
    window.addEventListener("scroll", throttledScroll);

    return () => {
      window.removeEventListener("scroll", throttledScroll);
    };
  }, [lastScrollY]);

  return (
    <nav
      className={
        hideNavbar
          ? "navbar navbar-expand-lg navbar-light mb-2 aos-init aos-animate hide"
          : "navbar navbar-expand-lg navbar-light mb-2 aos-init aos-animate"
      }
      data-aos="fade-in"
      id="main_navbar"
    >
      <div className="container">
        <Brand handleLinkClick={handleLinkClick} />
        <MobileMenuToggle
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
        />
        {/* Spinner - matches legacy structure exactly */}
        <div id="spinner" className="spinner">
          <br />
          <span>Your request is being processed.</span>
          <br />
          <span>Please be patient.</span>
          <br />
          <img
            className="img-spinner"
            src={require("../../assets/img/wait.gif")}
            alt="Please wait..Loading"
          />
        </div>
        <Menu
          secondaryMenu={secondaryMenu}
          isMobileMenuOpen={isMobileMenuOpen}
          handleLinkClick={handleLinkClick}
          setHoverState={setHoverState}
          setHoverLeaveState={setHoverLeaveState}
          isHovered={isHovered}
          isAuthenticated={isAuthenticated}
        />
      </div>
    </nav>
  );
}

Brand.propTypes = {
  handleLinkClick: PropTypes.func.isRequired,
};

function Brand({ handleLinkClick }) {
  return (
    <Link
      to="/"
      className="navbar-brand"
      aria-label="Experian.com"
      onClick={handleLinkClick}
    >
      <img
        src={require("../../assets/img/experian-nav-bar-logo.png")}
        alt="Experian Logo"
      />
    </Link>
  );
}

MobileMenuToggle.propTypes = {
  isMobileMenuOpen: PropTypes.bool.isRequired,
  toggleMobileMenu: PropTypes.func.isRequired,
};

function MobileMenuToggle({ isMobileMenuOpen, toggleMobileMenu }) {
  return (
    <button
      className={`navbar-toggler ${isMobileMenuOpen ? "" : "collapsed"}`}
      type="button"
      onClick={toggleMobileMenu}
      aria-controls="navbarResponsive"
      aria-expanded={isMobileMenuOpen ? "true" : "false"}
    >
      <span className="my-1 mx-2 close">X</span>
      <span className="navbar-toggler-icon">
        <svg viewBox="0 0 30 30" width="30" height="30">
          <path
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeMiterlimit="10"
            d="M4 7h22M4 15h22M4 23h22"
          />
        </svg>
      </span>
    </button>
  );
}

Menu.propTypes = {
  secondaryMenu: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
      subMenu: PropTypes.array.isRequired,
    })
  ).isRequired,
  isMobileMenuOpen: PropTypes.bool.isRequired,
  handleLinkClick: PropTypes.func.isRequired,
  setHoverState: PropTypes.func.isRequired,
  setHoverLeaveState: PropTypes.func.isRequired,
  isHovered: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
};

function Menu({
  secondaryMenu,
  isMobileMenuOpen,
  handleLinkClick,
  setHoverState,
  setHoverLeaveState,
  isHovered,
  isAuthenticated,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  // Check if we're on an NMVTIS page based on URL path
  const isNmvtisPage = location.pathname.includes('/nmvtis') || 
                       location.pathname.includes('/nmvtisreport') ||
                       location.pathname.includes('/nmvtisReports');
  
  // Determine login type: check localStorage first, then URL path
  const loginType = localStorage.getItem("loginType") === "nmvtis" || isNmvtisPage ? "nmvtis" : null;
  const logout = (e) => {
    e.preventDefault();
    dispatch(clearState());
    
    // Store loginType before clearing localStorage
    const storedLoginType = localStorage.getItem("loginType");
    
    // Clean up video iframes before logout to prevent VideoJS warnings and errors
    const videoIframes = document.querySelectorAll('iframe[src*="brightcove"], iframe[src*="videojs"], iframe[src*="bcove.video"], iframe[src*="players.brightcove"]');
    videoIframes.forEach((iframe) => {
      try {
        // Remove src to stop video playback and prevent warnings/errors
        iframe.src = 'about:blank';
        // Remove the iframe from DOM
        iframe.remove();
      } catch (err) {
        // Ignore errors during cleanup
        console.debug('Error cleaning up video iframe:', err);
      }
    });
    
    // Clear all localStorage items
    localStorage.clear();
    
    // Clear all sessionStorage items (including report HTML cache)
    // This ensures clean state on next login
    try {
      sessionStorage.clear();
    } catch (e) {
      // If clear() fails (some browsers), remove specific keys
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (err) {
          // Ignore individual removal errors
        }
      });
    }
    
    // Navigate after clearing storage
    if (storedLoginType === "nmvtis") {
      navigate("/nmvtisLogout");
      navigate(0);
    } else {
      navigate("/");
      navigate(0);
    }
  };
  return (
    <div
      className={`navbar-collapse collapse pl-4 ${
        isMobileMenuOpen ? "show" : ""
      }`}
      id="navbarResponsive"
    >
      <ul className="navbar-nav">
        {secondaryMenu.map((element) =>
          element.authentication === "members" && isAuthenticated ? (
            <MenuItem
              key={element.id}
              element={element}
              handleLinkClick={handleLinkClick}
              setHoverState={setHoverState}
              setHoverLeaveState={setHoverLeaveState}
              isHovered={isHovered}
              isMobileMenuOpen={isMobileMenuOpen}
            />
          ) : element.authentication === "both" && isAuthenticated ? (
            <MenuItem
              key={element.id}
              element={element}
              handleLinkClick={handleLinkClick}
              setHoverState={setHoverState}
              setHoverLeaveState={setHoverLeaveState}
              isHovered={isHovered}
              isMobileMenuOpen={isMobileMenuOpen}
            />
          ) : element.authentication === "both" && !isAuthenticated ? (
            <MenuItem
              key={element.id}
              element={element}
              handleLinkClick={handleLinkClick}
              setHoverState={setHoverState}
              setHoverLeaveState={setHoverLeaveState}
              isHovered={isHovered}
              isMobileMenuOpen={isMobileMenuOpen}
            />
          ) : (
            ""
          )
        )}
      </ul>
      <ul className="navbar-nav ml-auto">
        <li className="nav-item nav-level-1">
          {isAuthenticated ? (
            <Link
              className="nav-link"
              to={
                loginType === "nmvtis"
                  ? "/nmvtisLogout"
                  : "/"
              }
              aria-label="Client Sign Out"
              onClick={logout}
            >
              Client Sign Out
            </Link>
          ) : (
            <Link
              className="nav-link"
              to={
                loginType === "nmvtis"
                  ? "/nmvtis.do"
                  : "/login.do"
              }
              aria-label="Client Sign In"
              onClick={handleLinkClick}
            >
              Client Sign In
            </Link>
          )}
        </li>
      </ul>
    </div>
  );
}

MenuItem.propTypes = {
  element: PropTypes.shape({
    name: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    subMenu: PropTypes.arrayOf(
      PropTypes.shape({
        sid: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
        target: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  handleLinkClick: PropTypes.func.isRequired,
  setHoverState: PropTypes.func.isRequired,
  setHoverLeaveState: PropTypes.func.isRequired,
  isHovered: PropTypes.bool.isRequired,
  isMobileMenuOpen: PropTypes.bool.isRequired,
};

function MenuItem({
  element,
  handleLinkClick,
  setHoverState,
  setHoverLeaveState,
  isHovered,
  isMobileMenuOpen,
}) {
  const isAuthenticated = useAuth();
  return (
    <li
      className={
        element.subMenu.length > 0 ? "nav-item dropdown overlay" : "nav-item"
      }
      role="none"
      onMouseEnter={setHoverState}
      onMouseLeave={setHoverLeaveState}
      onClick={setHoverLeaveState}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          setHoverLeaveState();
        }
      }}
    >
      {element.subMenu.length > 0 ? (
        <Link
          className="nav-link dropdown-toggle"
          aria-current="page"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          to={element.link}
          onClick={handleLinkClick}
        >
          {element.name}
          <span className="custom-arrow"></span>
        </Link>
      ) : (
        <Link
          className="nav-link"
          aria-current="page"
          aria-expanded="false"
          to={element.link}
          onClick={handleLinkClick}
        >
          {element.name}
        </Link>
      )}
      {element.subMenu.length > 0 && (isHovered || isMobileMenuOpen) && (
        <ul className="header_mobmenu dropdown-menu ">
          {element.subMenu.map((subelement) =>
            subelement.authentication === "members" && isAuthenticated ? (
              <li key={subelement.sid} role="none">
                <Link
                  className="dropdown-item"
                  to={subelement.link}
                  target={subelement.target}
                  role="menuitem"
                  onClick={handleLinkClick}
                >
                  {subelement.name}
                </Link>
              </li>
            ) : subelement.authentication === "both" && isAuthenticated ? (
              <li key={subelement.sid} role="none">
                {subelement.link === "/dashboard" ? (
                  <a
                    className={`dropdown-item ${subelement.link === "/dashboard" || subelement.link === "/reportUsageDashboard" ? "pleaseWait" : ""}`}
                    href="/members/dashboard"
                    role="menuitem"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(e);
                      window.location.href = "/members/dashboard";
                    }}
                  >
                    {subelement.name}
                  </a>
                ) : (
                  <Link
                    className={`dropdown-item ${subelement.link === "/reportUsageDashboard" ? "pleaseWait" : ""}`}
                    to={subelement.link}
                    target={subelement.target}
                    role="menuitem"
                    onClick={handleLinkClick}
                  >
                    {subelement.name}
                  </Link>
                )}
              </li>
            ) : subelement.authentication === "both" && !isAuthenticated ? (
              <li key={subelement.sid} role="none">
                {subelement.link === "/dashboard" ? (
                  <a
                    className={`dropdown-item ${subelement.link === "/dashboard" || subelement.link === "/reportUsageDashboard" ? "pleaseWait" : ""}`}
                    href="/members/dashboard"
                    role="menuitem"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(e);
                      window.location.href = "/members/dashboard";
                    }}
                  >
                    {subelement.name}
                  </a>
                ) : (
                  <Link
                    className={`dropdown-item ${subelement.link === "/reportUsageDashboard" ? "pleaseWait" : ""}`}
                    to={subelement.link}
                    target={subelement.target}
                    role="menuitem"
                    onClick={handleLinkClick}
                  >
                    {subelement.name}
                  </Link>
                )}
              </li>
            ) : (
              ""
            )
          )}
        </ul>
      )}
    </li>
  );
}

function getSecondaryMenu(tokenExtract) {
  let roles = tokenExtract;
  try {
    let roleArray = tokenExtract.roles.split(",");
    if (tokenExtract && Array.isArray(roleArray)) {
      roles = roleArray;
    }
  } catch (e) {
    roles = [];
  }
  const membersMenu = [
    {
      id: 1,
      name: "AutoCheck Member Home",
      link: "/gateway",
      authentication: "both",
      subMenu: [],
    },
    {
      id: 2,
      name: "AutoCheck Reports",
      link: "#",
      authentication: "members",
      roles: ["ALL"],
      subMenu: [
        {
          sid: 1,
          name: "Run Reports",
          link: "/singleVINsearch",
          authentication: "both",
        },
        {
          sid: 2,
          name: "View Current Reports",
          link: "/dashboard",
          authentication: "both",
        },
        {
          sid: 3,
          name: "90 Day Usage",
          link: "/reportUsageDashboard",
          authentication: "both",
          roles: ["NINETY_DAY_USAGE"],
        },
        {
          sid: 4,
          name: "Sample Reports / Glossary",
          link: "/sample-reports/full",
          authentication: "both",
        },
        {
          sid: 5,
          name: "AutoCheck Elite",
          link: "/eliteReports",
          authentication: "both",
          roles: ["ELITE_REPORTS"],
        },
        {
          sid: 6,
          name: "NMVTIS Reports",
          link: "/nmvtisReports",
          authentication: "both",
          roles: ["NMVTIS_USER"],
        },
      ],
    },
    {
      id: 3,
      name: "Manage Accounts",
      authentication: "members",
      link: "#",
      roles: ["ALL"],
      subMenu: [
        {
          sid: 1,
          name: "Account Profile",
          link: "/manageAccount",
          authentication: "both",
          roles: ["ALL"],
        },
        {
          sid: 2,
          name: "Buyback Protection",
          link: "/buyback",
          authentication: "both",
          roles: ["ALL"],
          notIncludes: ["COX_SUPPORT", "COX_DEALER"],
        },
        {
          sid: 3,
          name: "Fast Link Integration",
          link: "/manageFreeLink",
          authentication: "both",
          roles: ["MANAGE_FAST_LINK"],
          notIncludes: ["COX_SUPPORT", "COX_DEALER"],
        },
        {
          sid: 4,
          name: "ROI Calculator",
          link: "/roiCalculator",
          authentication: "both",
          roles: ["ROI_TOOL"],
          notIncludes: ["COX_SUPPORT", "COX_DEALER"],
        },
        {
          sid: 5,
          name: "Service Data",
          link: "/serviceData",
          authentication: "both",
          roles: ["ALL"],
          notIncludes: ["COX_SUPPORT", "COX_DEALER"],
        },
        {
          sid: 6,
          name: "Refer a Friend",
          link: "/referFriend",
          authentication: "both",
          roles: ["ALL"],
          notIncludes: ["COX_SUPPORT", "COX_DEALER"],
        },
        {
          sid: 7,
          name: "FAQs",
          link: "/faq",
          authentication: "both",
          roles: ["ALL"],
          notIncludes: ["COX_SUPPORT", "COX_DEALER"],
        },
      ],
    },
    {
      id: 4,
      name: "Help",
      authentication: "both",
      link: "#",
      subMenu: [
        {
          sid: 1,
          name: "About us",
          link: "https://www.experian.com/automotive/about-experian-automotive?cmpid=auto_web_acmembers_hp",
          authentication: "both",
          target: "_blank",
          notIncludes: ["COX_SUPPORT"],
        },
        {
          sid: 2,
          name: "Contact us",
          link: "/contact-us",
          authentication: "both",
        },
        {
          sid: 3,
          name: "Free Flood Risk Check",
          authentication: "both",
          link: `${process.env.REACT_APP_PUBLIC_DOMAIN}vehiclehistory/flood-risk-check`,
          target: "_blank",
          roles: ["ALL"],
          notIncludes: ["COX_SUPPORT"],
        },
        {
          sid: 4,
          name: "Lift Tool",
          link: "/upload-lifttool-file",
          authentication: "members",
          roles: ["LIFT_TOOL"],
        },
        {
          sid: 5,
          name: "Data Correction Request",
          link: "/correction-Request",
          authentication: "members",
          roles: ["ALL"],
          notIncludes: ["COX_SUPPORT"],
        },
        {
          sid: 6,
          name: "Support Request",
          link: "/support-request",
          authentication: "members",
          roles: ["COX_SUPPORT"],
        },
      ],
    },
  ];
  const nmvtisMenu = [
    {
      id: 6,
      name: "NMVTIS Reports",
      authentication: "members",
      link: "/nmvtisReports",
      subMenu: [],
      roles: ["ALL"],
    },
    {
      id: 1,
      name: "Help",
      authentication: "both",
      link: "/nmvtishelp",
      subMenu: [],
    },
  ];
  function filterByRoles(menu) {
    // Check if member_type is 18 or 19
    const memberType = tokenExtract?.member_type;
    const isMemberType18 = memberType === 18 || memberType === "18";
    const isMemberType19 = memberType === 19 || memberType === "19";
    const isRestrictedMemberType = isMemberType18 || isMemberType19;

    // First filter by roles
    let filteredMenu = menu.filter((item) => {
      // Hide "AutoCheck Reports" menu for member_type 18
      if (
        isMemberType18 &&
        item.id === 2 &&
        item.name === "AutoCheck Reports"
      ) {
        return false;
      }
      // Special case: Show "Manage Accounts" for member_type 18 or 19 even without ALL role
      if (
        isRestrictedMemberType &&
        item.id === 3 &&
        item.name === "Manage Accounts"
      ) {
        return true;
      }
      return (
        !item.roles ||
        item.roles.length === 0 ||
        item.roles.some((role) => roles.includes(role))
      );
    });

    // Then filter out items with notIncludes
    filteredMenu = filteredMenu.map((item) => {
      let subMenu = item.subMenu || [];

      // Special case: For member_type 18 or 19, only show "Account Profile" in Manage Accounts submenu
      if (
        isRestrictedMemberType &&
        item.id === 3 &&
        item.name === "Manage Accounts"
      ) {
        subMenu = subMenu.filter(
          (sub) => sub.sid === 1 && sub.name === "Account Profile"
        );
      } else {
        // First filter by roles
        subMenu = subMenu.filter(
          (sub) =>
            !sub.roles ||
            sub.roles.length === 0 ||
            sub.roles.some((role) => roles.includes(role))
        );
        // Then filter out by notIncludes
        subMenu = subMenu.filter(
          (sub) =>
            !sub.notIncludes ||
            !sub.notIncludes.some((role) => roles.includes(role))
        );
        // Filter out "Run Reports" for member_type 18
        if (isMemberType18) {
          subMenu = subMenu.filter(
            (sub) => !(sub.sid === 1 && sub.name === "Run Reports")
          );
        }
      }
      return { ...item, subMenu };
    });

    // Also filter out top-level items with notIncludes
    filteredMenu = filteredMenu.filter(
      (item) =>
        !item.notIncludes ||
        !item.notIncludes.some((role) => roles.includes(role))
    );

    return filteredMenu;
  }
  return localStorage.getItem("loginType") === "nmvtis"
    ? filterByRoles(nmvtisMenu)
    : filterByRoles(membersMenu);
}

export default Header;
